import { useEffect, useState } from 'react';

import type { Medium } from '../../domain/types';
import { useT } from '../../i18n';
import { appendAppLog } from '../../platform/appLog';
import { readBase64 } from '../../platform/storage';
import { medienPfad } from '../../server/classic/medien';
import styles from './Sheets.module.css';

/**
 * Die Anhänge einer Station — Bilder und PDF, die der Server zur Prüfung mitliefert.
 *
 * ⚠️ **Von der Platte, nie aus dem Netz.** Die Dateien werden beim Herunterladen der Prüfung
 * geholt (`server/classic/medien.ts`); während einer Prüfung steht das iPad womöglich ohne
 * Netz. Was hier fehlt, fehlt — und sagt es, statt leer zu bleiben.
 */
export function VignetteMaterial({ examId, medien }: { examId: string; medien: Medium[] }) {
  const t = useT();
  if (medien.length === 0) return null;

  return (
    <div className={styles.block}>
      <div className={styles.blockLabel}>{t.vignette.material}</div>
      <div className={styles.materialListe}>
        {medien.map((m) => (
          <Anhang key={m.id} examId={examId} medium={m} />
        ))}
      </div>
    </div>
  );
}

function Anhang({ examId, medium }: { examId: string; medium: Medium }) {
  const t = useT();
  const [quelle, setQuelle] = useState<string | null>(null);
  const [fehlt, setFehlt] = useState(false);

  useEffect(() => {
    if (!medium.istBild) return;
    let lebt = true;
    void (async () => {
      try {
        const base64 = await readBase64(medienPfad(examId, medium.id, medium.mimeType));
        if (lebt) setQuelle('data:' + medium.mimeType + ';base64,' + base64);
      } catch (f) {
        if (lebt) setFehlt(true);
        await appendAppLog('Anhang ' + medium.id + ' liegt nicht auf dem Gerät: ' + String(f), 'error');
      }
    })();
    return () => {
      lebt = false;
    };
  }, [examId, medium.id, medium.istBild, medium.mimeType]);

  return (
    <figure className={styles.material}>
      {quelle ? (
        <img src={quelle} alt={medium.name} className={styles.materialBild} />
      ) : (
        // Kein Bild: entweder ein PDF (das der WebView hier nicht zeigen soll) oder eine Datei,
        // die es nicht auf das Gerät geschafft hat. Beides steht als Zeile da — ein stiller
        // Verlust wäre schlimmer als eine sichtbare Lücke.
        <div className={styles.materialLeer} data-fehlt={fehlt}>
          {fehlt ? t.vignette.materialMissing : medium.mimeType}
        </div>
      )}
      <figcaption className={styles.materialName}>
        {medium.name}
        {medium.description !== '' ? <span> · {medium.description}</span> : null}
      </figcaption>
    </figure>
  );
}
